import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { toast } from 'react-toastify';

import Table from '../Table';
import AuthorizationPdfPreviewModal from './AuthorizationPdfPreviewModal';

// .Env value
const collaboratorName = process.env.REACT_APP_COLLABORATOR_NAME;

const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('fr-FR');
};

const statusBadge = (status) => {
    switch (status) {
        case 'SIGNED':
            return 'bg-green-100 text-green-700';
        case 'DRAFT':
            return 'bg-yellow-100 text-yellow-700';
        default:
            return 'bg-gray-100 text-gray-600';
    }
};

const ListAuthorizationConsents = () => {
    const consents = useStoreState(s => s.global.authorizationConsents);
    const collaborators = useStoreState(s => s.global.collaborators);

    const getListCollaborators = useStoreActions(
        a => a.middleware.CollaboratorMiddleware.getListCollaborators
    );
    const getListAuthorizationConsents = useStoreActions(
        a => a.middleware.AuthorizationMiddleware.getListAuthorizationConsents
    );
    const setAuthorizationConsents = useStoreActions(
        a => a.global.setAuthorizationConsents
    );
    const getPresignedUrl = useStoreActions(
        a => a.middleware.FileS3.getPresignedUrl
    );

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [search, setSearch] = useState('');

    const load = useCallback(async () => {
        try {
            // load collaborators
            if (collaborators.length === 0) {
                const payload = {
                    nameCollab: collaboratorName.toString(),
                };
                await getListCollaborators(payload);
            }

            // load consents
            const res = await getListAuthorizationConsents({ limit: 200 });
            setAuthorizationConsents(res?.items || []);
        } catch (e) {
            console.log(e);
            toast.error("❗️ Erreur lors du chargement des autorisations");
        }
    }, [
        getListAuthorizationConsents,
        setAuthorizationConsents,
        getListCollaborators,
        collaborators.length
    ]);

    useEffect(() => {
        load();
    }, [load]);

    // Get collaborator name by ID on authorization consent list
    const collaboratorsMap = useMemo(() => {
        const map = {};
        collaborators.forEach(c => {
            map[c.id] = `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim();
        });
        return map;
    }, [collaborators]);

    const getCollaboratorName = (id) =>
        collaboratorsMap[id] || id || '—';

    // 🔍 filtre
    const filteredConsents = useMemo(() => {
        const s = search.trim().toLowerCase();
        if (!s) return consents;

        return consents.filter(c =>
            c.tenantName?.toLowerCase().includes(s) ||
            c.tenantEmail?.toLowerCase().includes(s) ||
            c.technicianId?.toLowerCase().includes(s)
        );
    }, [consents, search]);

    // 📄 open PDF preview
    const openPdfPreview = async (consent) => {
        if (!consent?.pdfKey) return;

        try {
            const url = await getPresignedUrl(consent.pdfKey);
            if (!url) {
                toast.error("Impossible de générer le lien du PDF");
                return;
            }
            setPreviewUrl(url);
            setPreviewOpen(true);
        } catch (e) {
            console.error(e);
            toast.error("Erreur lors de l'ouverture du PDF");
        }
    };


    return (
        <section className="md:pt-14 px-4 md:px-9">
            {/* header */}
            <div className="flex flex-col gap-4 mb-4 md:flex-row md:items-center md:justify-between">
                <div>
                    <h3 className="font-bold text-xl">Autorisations créées</h3>
                    <p className="text-sm text-gray-500">
                        Historique des autorisations générées et signées
                    </p>
                </div>

                <input
                    type="text"
                    placeholder="Rechercher (locataire, email, technicien)…"
                    className="border rounded-lg px-3 py-2 text-sm w-72"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            {/* table */}
            <Table>
                <thead>
                    <tr>
                        <th>Locataire</th>
                        <th>Email</th>
                        <th>Statut</th>
                        <th>Technicien</th>
                        <th>Créée le</th>
                        <th className='text-right'>Aperçu</th>
                    </tr>
                </thead>

                <tbody>
                    {filteredConsents.map(c => (
                        <tr key={c.id}>
                            <td>{c.tenantName || '—'}</td>
                            <td>{c.tenantEmail || '—'}</td>

                            <td>
                                <span
                                    className={`px-2 py-1 rounded-full text-xs font-semibold ${statusBadge(c.status)}`}
                                >
                                    {c.status}
                                </span>
                            </td>

                            <td className="text-xs text-gray-600">
                                {getCollaboratorName(c.technicianId)}
                            </td>

                            <td>{formatDate(c.createdAt)}</td>
                            <td className="text-right">
                                {c.pdfKey ? (
                                    <button
                                        onClick={() => openPdfPreview(c)}
                                        className="text-blue-admin underline text-sm"
                                    >
                                        Voir PDF
                                    </button>
                                ) : (
                                    '—'
                                )}
                            </td>
                        </tr>
                    ))}

                    {!filteredConsents.length && (
                        <tr>
                            <td colSpan={6} className="text-center italic text-gray-500 py-6">
                                Aucune autorisation trouvée
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
            <AuthorizationPdfPreviewModal
                visible={previewOpen}
                pdfUrl={previewUrl}
                onClose={() => setPreviewOpen(false)}
            />
        </section>
    );
};

export default ListAuthorizationConsents;
